/**
 * Role-Based Access Control (RBAC) Package
 *
 * Implements: BPMN Gateway "Check User Permissions"
 * ArchiMate: Application Function (Authorization)
 *
 * Maps Google Workspace groups to roles and enforces permissions.
 *
 * @module @proxmox-control-plane/rbac
 */
// ============================================================================
// Role-Permission Matrix
// ============================================================================
/**
 * Permission matrix defining which permissions each role has
 *
 * Business Capability: Access Control
 * ArchiMate: Business Rule (Permission Matrix)
 *
 * Role Hierarchy: superadmin > admin > operations > developer > viewer
 */
const ROLE_PERMISSIONS = {
    /**
     * Superadmin: Complete system access including user management
     * Reserved for system administrators (jorgen.scheel@ecobyte.net)
     */
    superadmin: [
        'vm.view',
        'vm.create',
        'vm.start',
        'vm.stop',
        'vm.delete',
        'vm.modify',
        'vm.clone',
        'vm.snapshot',
        'node.view',
        'node.configure',
        'storage.view',
        'storage.configure',
        'network.view',
        'network.configure',
        'template.view',
        'template.manage',
        'user.view',
        'user.manage',
        'role.assign',
        'system.admin',
    ],
    /**
     * Admin: Full access to all operations
     * Can manage VMs, nodes, storage, network, and templates
     * Can view users but cannot modify roles
     */
    admin: [
        'vm.view',
        'vm.create',
        'vm.start',
        'vm.stop',
        'vm.delete',
        'vm.modify',
        'vm.clone',
        'vm.snapshot',
        'node.view',
        'node.configure',
        'storage.view',
        'storage.configure',
        'network.view',
        'network.configure',
        'template.view',
        'template.manage',
        'user.view',
    ],
    /**
     * Operations: Operational control over VMs and nodes
     * Can start, stop, and monitor VMs but cannot create or delete
     */
    operations: [
        'vm.view',
        'vm.start',
        'vm.stop',
        'vm.modify',
        'node.view',
        'storage.view',
        'network.view',
    ],
    /**
     * Developer: Development environment management
     * Can create and manage own VMs but limited access to infrastructure
     */
    developer: [
        'vm.view',
        'vm.create',
        'vm.start',
        'vm.stop',
        'vm.modify',
        'vm.clone',
        'template.view',
    ],
    /**
     * Viewer: Read-only access
     * Can view VMs, nodes, and infrastructure status
     */
    viewer: ['vm.view', 'node.view', 'storage.view', 'network.view', 'template.view'],
};
// ============================================================================
// Group-to-Role Mapping
// ============================================================================
/**
 * Default Google Workspace group-to-role mappings
 *
 * BPMN Activity: "Map User Groups to Role"
 * ArchiMate: Business Rule (Group Mapping)
 */
const DEFAULT_GROUP_MAPPINGS = {
    'vmfactory-admins': 'admin',
    'vmfactory-operations': 'operations',
    'vmfactory-developers': 'developer',
    'vmfactory-viewers': 'viewer',
};
// ============================================================================
// Core RBAC Functions
// ============================================================================
/**
 * Maps a Google Workspace group to a role
 *
 * BPMN Activity: "Map User Groups to Role"
 * ArchiMate: Application Function (Group Mapping)
 *
 * @param group - Google Workspace group name
 * @param config - Optional RBAC configuration
 * @returns Mapped role or null if no mapping exists
 */
export function mapGroupToRole(group, config) {
    const mappings = config?.groupMappings || DEFAULT_GROUP_MAPPINGS;
    return mappings[group] ?? null;
}
/**
 * Determines the highest role from a list of groups or email
 *
 * BPMN Gateway: "Determine User Role"
 * ArchiMate: Application Function (Role Resolution)
 *
 * Role hierarchy: superadmin > admin > operations > developer > viewer
 *
 * Superadmin is assigned based on email address (jorgen.scheel@ecobyte.net)
 *
 * @param groups - Array of Google Workspace groups
 * @param config - Optional RBAC configuration
 * @param email - User email for superadmin check
 * @returns Highest role based on group membership or email
 */
export function determineRole(groups, config, email) {
    // Superadmin check by email
    if (email === 'jorgen.scheel@ecobyte.net') {
        return 'superadmin';
    }
    const roleHierarchy = ['superadmin', 'admin', 'operations', 'developer', 'viewer'];
    // Check each role in hierarchy order
    for (const hierarchyRole of roleHierarchy) {
        for (const group of groups) {
            const mappedRole = mapGroupToRole(group, config);
            if (mappedRole === hierarchyRole) {
                return hierarchyRole;
            }
        }
    }
    // Return default role if no groups match
    return config?.defaultRole || 'viewer';
}
/**
 * Gets all permissions for a given role
 *
 * BPMN Activity: "Retrieve Role Permissions"
 * ArchiMate: Application Function (Permission Retrieval)
 *
 * @param role - User role
 * @returns Array of permissions granted to the role
 */
export function getPermissionsForRole(role) {
    return ROLE_PERMISSIONS[role] || [];
}
/**
 * Checks if a role has a specific permission
 *
 * BPMN Gateway: "Check User Permission"
 * ArchiMate: Application Function (Authorization Check)
 *
 * @param role - User role
 * @param permission - Permission to check
 * @returns True if role has permission, false otherwise
 */
export function hasPermission(role, permission) {
    const permissions = ROLE_PERMISSIONS[role];
    return permissions ? permissions.includes(permission) : false;
}
/**
 * Checks if a role has any of the specified permissions
 *
 * @param role - User role
 * @param permissions - Array of permissions to check
 * @returns True if role has at least one permission
 */
export function hasAnyPermission(role, permissions) {
    return permissions.some((permission) => hasPermission(role, permission));
}
/**
 * Checks if a role has all of the specified permissions
 *
 * @param role - User role
 * @param permissions - Array of permissions to check
 * @returns True if role has all permissions
 */
export function hasAllPermissions(role, permissions) {
    return permissions.every((permission) => hasPermission(role, permission));
}
/**
 * Asserts that a role has a specific permission
 *
 * Throws an error if permission is denied (useful for strict mode)
 *
 * @param role - User role
 * @param permission - Permission to check
 * @throws Error if permission is denied
 */
export function assertPermission(role, permission) {
    if (!hasPermission(role, permission)) {
        throw new Error(`Permission denied: Role '${role}' does not have permission '${permission}'`);
    }
}
/**
 * Creates an authenticated user object from Cloudflare Access user
 *
 * BPMN Activity: "Create Authenticated Session"
 * ArchiMate: Application Function (Session Creation)
 *
 * @param identity - Cloudflare Access user identity
 * @param config - Optional RBAC configuration
 * @returns Authenticated user with role and permissions
 */
export function createAuthenticatedUser(identity, config) {
    const role = determineRole(identity.groups, config, identity.email);
    const permissionsList = getPermissionsForRole(role);
    const permissions = new Set(permissionsList);
    return {
        identity,
        role,
        permissions,
        sessionMetadata: {
            authenticatedAt: new Date(),
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
        },
    };
}
/**
 * Checks if an authenticated user has a specific permission
 *
 * @param user - Authenticated user
 * @param permission - Permission to check
 * @returns True if user has permission
 */
export function userHasPermission(user, permission) {
    return user.permissions.has(permission);
}
/**
 * Checks if an authenticated user has any of the specified permissions
 *
 * @param user - Authenticated user
 * @param permissions - Array of permissions to check
 * @returns True if user has at least one permission
 */
export function userHasAnyPermission(user, permissions) {
    return permissions.some((permission) => user.permissions.has(permission));
}
/**
 * Checks if an authenticated user has all of the specified permissions
 *
 * @param user - Authenticated user
 * @param permissions - Array of permissions to check
 * @returns True if user has all permissions
 */
export function userHasAllPermissions(user, permissions) {
    return permissions.every((permission) => user.permissions.has(permission));
}
/**
 * Asserts that an authenticated user has a specific permission
 *
 * @param user - Authenticated user
 * @param permission - Permission to check
 * @throws Error if permission is denied
 */
export function assertUserPermission(user, permission) {
    if (!user.permissions.has(permission)) {
        throw new Error(`Permission denied: User '${user.identity.email}' (role: ${user.role}) does not have permission '${permission}'`);
    }
}
// ============================================================================
// Utility Functions
// ============================================================================
/**
 * Gets a human-readable description of a role
 *
 * @param role - User role
 * @returns Role description
 */
export function getRoleDescription(role) {
    const descriptions = {
        superadmin: 'Complete system access including user and role management',
        admin: 'Full access to all VM operations and system configuration',
        operations: 'Operational control - can start, stop, and monitor VMs',
        developer: 'Development access - can create and manage own VMs',
        viewer: 'Read-only access to view VMs and node status',
    };
    return descriptions[role] || 'Unknown role';
}
/**
 * Gets a human-readable name for a permission
 *
 * @param permission - Permission
 * @returns Permission display name
 */
export function getPermissionName(permission) {
    const names = {
        'vm.view': 'View Virtual Machines',
        'vm.create': 'Create Virtual Machines',
        'vm.start': 'Start Virtual Machines',
        'vm.stop': 'Stop Virtual Machines',
        'vm.delete': 'Delete Virtual Machines',
        'vm.modify': 'Modify Virtual Machines',
        'vm.clone': 'Clone Virtual Machines',
        'vm.snapshot': 'Create VM Snapshots',
        'node.view': 'View Nodes',
        'node.configure': 'Configure Nodes',
        'storage.view': 'View Storage',
        'storage.configure': 'Configure Storage',
        'network.view': 'View Network',
        'network.configure': 'Configure Network',
        'template.view': 'View Templates',
        'template.manage': 'Manage Templates',
        'user.view': 'View Users',
        'user.manage': 'Manage Users',
        'role.assign': 'Assign Roles',
        'system.admin': 'System Administration',
    };
    return names[permission] || permission;
}
/**
 * Compares two roles in the hierarchy
 *
 * @param role1 - First role
 * @param role2 - Second role
 * @returns Positive if role1 > role2, negative if role1 < role2, 0 if equal
 */
export function compareRoles(role1, role2) {
    const hierarchy = ['superadmin', 'admin', 'operations', 'developer', 'viewer'];
    const index1 = hierarchy.indexOf(role1);
    const index2 = hierarchy.indexOf(role2);
    return index2 - index1; // Reverse order (superadmin is highest)
}
/**
 * Checks if one role is higher than another in the hierarchy
 *
 * @param role1 - First role
 * @param role2 - Second role
 * @returns True if role1 is higher than role2
 */
export function isHigherRole(role1, role2) {
    return compareRoles(role1, role2) > 0;
}
//# sourceMappingURL=index.js.map